import { read, utils } from 'xlsx'
import { detectStudentColumns, isPrimaryValue, resolveClassName } from './classNameProcessor.js'

export const processExcelFile = async (file, onProgress) => {
    const data = await file.arrayBuffer()
    const workbook = read(data, { raw: true })

    // Filter out sheets we should skip
    const validSheets = workbook.SheetNames.filter((name) => !shouldSkipSheet(name))

    let studentsData = []
    let staffData = []
    let totalRows = 0

    // First, count total rows for all valid sheets
    for (const sheetName of validSheets) {
        const sheet = workbook.Sheets[sheetName]
        if (!sheet || !sheet['!ref']) continue
        const { headers, dataStartRow } = findHeadersAndData(sheet)
        if (Object.keys(headers).length === 0 || dataStartRow === null) continue
        const range = utils.decode_range(sheet['!ref'])
        totalRows += range.e.r - dataStartRow + 1
    }

    let processedRows = 0
    if (onProgress) onProgress(0)

    for (const sheetName of validSheets) {
        const sheet = workbook.Sheets[sheetName]

        if (!sheet || !sheet['!ref']) {
            console.warn(`Sheet ${sheetName} is empty or invalid`)
            continue
        }

        const { headers, dataStartRow } = findHeadersAndData(sheet)

        if (Object.keys(headers).length === 0 || dataStartRow === null) {
            console.warn(`No valid headers found in sheet: ${sheetName}`)
            continue
        }

        // Clean up headers
        const cleanedHeaders = {}
        for (const [col, header] of Object.entries(headers)) {
            // Skip "Authority Examples" column
            if (header.toLowerCase().includes('authority examples')) {
                continue
            }

            // Clean up common headers
            let cleanHeader = header
            if (header.toLowerCase().includes('id') && !header.toLowerCase().includes('email valid') && !header.toLowerCase().includes('valid')) {
                cleanHeader = 'ID'
            } else if (header.toLowerCase().includes('authority')) {
                cleanHeader = 'Authority'
            } else if (header.toLowerCase().includes('password')) {
                cleanHeader = 'Password'
            } else if (header.toLowerCase().includes('student access to email') || header.toLowerCase().includes('email valid') || header.toLowerCase().includes('valid email')) {
                cleanHeader = 'Valid Email'
            } else if (
                (header.toLowerCase().includes('username') || header.toLowerCase().includes('user name') || header.toLowerCase().includes('email')) &&
                !header.toLowerCase().includes('email valid') &&
                !header.toLowerCase().includes('valid')
            ) {
                cleanHeader = 'Email'
            } else if (header.toLowerCase().includes('example')) {
                continue
            }

            cleanedHeaders[col] = cleanHeader
        }

        // Custom extractData with progress
        const extractedData = extractDataWithProgress(sheet, cleanedHeaders, dataStartRow, totalRows, () => {
            processedRows++
            if (onProgress && totalRows > 0) onProgress(Math.min(100, Math.round((processedRows / totalRows) * 100)))
        })

        if (extractedData.length === 0) {
            console.warn(`No data found in sheet: ${sheetName}`)
            continue
        }

        // Determine if student or staff data
        const dataType = guessDataType(cleanedHeaders, sheetName)

        if (dataType === 'student' && studentsData.length === 0) {
            // Process class names for student data
            const processedData = processStudentClasses(extractedData)
            studentsData = processedData
        } else if (dataType === 'staff' && staffData.length === 0) {
            staffData = extractedData
        } else if (studentsData.length === 0 && !dataType) {
            // If we can't determine type and haven't found student data yet, assume it's student data
            const processedData = processStudentClasses(extractedData)
            studentsData = processedData
        } else if (staffData.length === 0 && !dataType) {
            // If we can't determine type and haven't found staff data yet, assume it's staff data
            staffData = extractedData
        }
    }
    if (onProgress) onProgress(100)

    return {
        studentsData,
        staffData,
        processedSheets: validSheets,
    }
}

// Custom extractData that reports progress
const extractDataWithProgress = (sheet, headers, dataStartRow, totalRows, onRow) => {
    if (!sheet || !headers || !dataStartRow) return []
    const range = utils.decode_range(sheet['!ref'])
    const data = []
    for (let r = dataStartRow; r <= range.e.r; r++) {
        const rowData = {}
        let hasValues = false
        let nonEmptyCount = 0
        Object.entries(headers).forEach(([col, header]) => {
            const cellAddress = utils.encode_cell({ r, c: Number.parseInt(col) })
            const cell = sheet[cellAddress]
            let value = cell && cell.v !== undefined && cell.v !== null ? cell.v : ''
            // Treat 'N/A' (case-insensitive) as empty string
            if (typeof value === 'string' && value.trim().toLowerCase() === 'n/a') {
                value = ''
            }
            if (value !== '') {
                rowData[header] = value
                hasValues = true
                nonEmptyCount++
            } else {
                rowData[header] = ''
            }
        })
        if (hasValues && nonEmptyCount >= 2) {
            data.push(rowData)
        }
        if (onRow) onRow()
    }
    return data
}

// Process student data: resolve the final class name and add the primary attribute.
// Every processed row gets:
//   - primary        boolean, from the early-access column
//   - classesName    the resolved class name (year + class joined per the naming rule)
//   - rawClassName   the class value exactly as written in the sheet
//   - Year           the year value as a string (only when the sheet has a year column under a different header)
// The original class column is also overwritten with the resolved name, which is what older backends read.
const processStudentClasses = (studentsData) => {
    console.log(`Processing ${studentsData?.length ?? 0} student records for class names and primary access...`)

    if (!studentsData || !studentsData.length) return []

    const columns = detectStudentColumns(studentsData)
    if (!columns.classColumn) {
        console.warn('No class column could be identified in the student data')
    }
    if (!columns.yearColumn) {
        console.warn('No year column could be identified in the student data')
    }

    return studentsData.map((student) => {
        const processed = { ...student }

        if (processed.Email) {
            processed.Email = String(processed.Email).toLowerCase().trim()
        }

        const rawClassName = columns.classColumn ? student[columns.classColumn] : ''
        const rawYear = columns.yearColumn ? student[columns.yearColumn] : ''
        const yearStr = rawYear === undefined || rawYear === null ? '' : String(rawYear).trim()
        const resolvedClassName = resolveClassName(yearStr, rawClassName)

        processed.primary = columns.earlyAccessColumn ? isPrimaryValue(student[columns.earlyAccessColumn]) : false
        processed.classesName = resolvedClassName
        processed.rawClassName = rawClassName === undefined || rawClassName === null ? '' : String(rawClassName).trim()

        if (columns.classColumn && resolvedClassName) {
            processed[columns.classColumn] = resolvedClassName
        }

        // The backend reads the year from "Year"/"year"; expose it under that key when the sheet used another header
        if (columns.yearColumn && columns.yearColumn !== 'Year' && columns.yearColumn !== 'year') {
            processed.Year = yearStr
        }

        return processed
    })
}

// Detects a column that likely holds passwords, so the "Use Excel Passwords"
// setting can be shown/defaulted based on what the sheet actually contains.
export const findPasswordColumn = (data) => {
    if (!data || !data.length) return null

    const columns = Object.keys(data[0])
    return columns.find((col) => col.toLowerCase().includes('password') || col.toLowerCase().includes('pwd') || col.toLowerCase().includes('pass'))
}

const shouldSkipSheet = (sheetName) => {
    const lowerName = sheetName.toLowerCase()
    return lowerName.includes('checklist') || lowerName.includes('instruction') || lowerName.includes('readme') || lowerName.includes('validation')
}

const findHeadersAndData = (sheet) => {
    const range = utils.decode_range(sheet['!ref'])
    const headerRows = []
    let dataStartRow = null
    let headerRow = null

    // Look for rows that might be headers (non-empty, with text values)
    for (let r = range.s.r; r <= Math.min(range.e.r, range.s.r + 20); r++) {
        const rowValues = []
        let nonEmptyCount = 0

        for (let c = range.s.c; c <= range.e.c; c++) {
            const cellAddress = utils.encode_cell({ r, c })
            const cell = sheet[cellAddress]

            if (cell && cell.v !== undefined && cell.v !== null && cell.v !== '') {
                nonEmptyCount++
                rowValues.push({ col: c, value: cell.v })
            }
        }

        // If row has several non-empty cells, it might be a header row
        if (nonEmptyCount >= 3) {
            headerRows.push({ row: r, values: rowValues })

            // Check if any values look like column headers - be more flexible with matching
            const possibleHeaderPatterns = [
                'first',
                'last',
                'name',
                'id',
                'email',
                'mail',
                'username',
                'user',
                'gender',
                'sex',
                'class',
                'grade',
                'year',
                'section',
                'classroom',
                'authority',
                'role',
                'password',
                'pwd',
                'pass',
                'dob',
                'birth',
                'student',
                'pupil',
                'staff',
                'teacher',
                'group',
                'fs',
            ]

            const headerMatches = rowValues.filter((v) => {
                if (!v.value) return false
                const cellValue = String(v.value).toLowerCase()
                return possibleHeaderPatterns.some((pattern) => cellValue.includes(pattern))
            })

            // If we find at least 2 potential header matches, consider this a header row
            if (headerMatches.length >= 2) {
                headerRow = r
                dataStartRow = r + 1 // Data starts after header
                break
            }
        }
    }

    // If we found a header row, extract the headers
    if (headerRow !== null) {
        const headers = {}
        const headerCells = headerRows[headerRows.length - 1].values

        headerCells.forEach((cell) => {
            headers[cell.col] = String(cell.value).trim()
        })

        return { headers, dataStartRow, headerRow }
    }

    // Fallback: try to use the first non-empty row as header
    if (headerRows.length > 0) {
        const headers = {}
        const headerCells = headerRows[0].values

        headerCells.forEach((cell) => {
            headers[cell.col] = String(cell.value).trim()
        })

        return { headers, dataStartRow: headerRows[0].row + 1, headerRow: headerRows[0].row }
    }

    return { headers: {}, dataStartRow: null, headerRow: null }
}

const guessDataType = (headers, sheetName) => {
    const headerText = Object.values(headers).join(' ').toLowerCase()
    const sheetNameLower = sheetName.toLowerCase()

    // Check sheet name first
    if (sheetNameLower.includes('student') || sheetNameLower.includes('pupil')) {
        return 'student'
    }

    if (sheetNameLower.includes('staff') || sheetNameLower.includes('teacher')) {
        return 'staff'
    }

    // Then check headers
    if (
        headerText.includes('student') ||
        headerText.includes('pupil') ||
        headerText.includes('class') ||
        headerText.includes('year') ||
        headerText.includes('grade') ||
        headerText.includes('fs')
    ) {
        return 'student'
    }

    if (headerText.includes('staff') || headerText.includes('teacher') || headerText.includes('faculty') || headerText.includes('subject')) {
        return 'staff'
    }

    return null
}
